import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function RaffleDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [raffle, setRaffle] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    loadRaffle();
    loadPackages();
  }, [id]);

  const loadRaffle = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/raffles/${id}`);
      setRaffle(response.data.data);
      setEditData({
        title: response.data.data.title,
        description: response.data.data.description,
        end_date: response.data.data.end_date,
        max_entries_per_person: response.data.data.max_entries_per_person || ''
      });
    } catch (error) {
      console.error('Error loading raffle:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPackages = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/raffles/${id}/packages`);
      setPackages(response.data.data);
    } catch (error) {
      console.error('Error loading packages:', error);
    }
  };

  const handleAction = async (endpoint, confirmMessage, successMessage) => {
    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/raffles/${id}${endpoint}`);

      if (endpoint === '/close' && response.data.data) {
        alert(`Winner selected: ${response.data.data.winner.name} - Ticket: ${response.data.data.winningTicket}`);
      } else if (successMessage) {
        alert(successMessage);
      }

      loadRaffle();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Error: ${errorMsg}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to DELETE this raffle? This cannot be undone!')) {
      return;
    }

    setActionLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/raffles/${id}`);
      alert('Raffle deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Error: ${errorMsg}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/raffles/${id}`, editData);
      alert('Raffle updated successfully');
      setEditMode(false);
      loadRaffle();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      alert(`Error: ${errorMsg}`);
    } finally {
      setActionLoading(false);
    }
  };

  const hasEntries = raffle?.statistics?.entry_count > 0;

  if (loading) return <div className="container">Loading...</div>;
  if (!raffle) return <div className="container">Raffle not found</div>;

  return (
    <div className="container">
      <button onClick={() => navigate('/dashboard')} style={{ marginBottom: '20px' }}>
        ← Back to Dashboard
      </button>

      <div className="card">
        {!editMode ? (
          <>
            <h1>{raffle.title}</h1>
            <p>{raffle.description}</p>

            <div style={{ margin: '20px 0' }}>
              <p><strong>Status:</strong> <span style={{
                textTransform: 'uppercase',
                padding: '4px 12px',
                borderRadius: '4px',
                background: raffle.status === 'active' ? '#d4edda' :
                           raffle.status === 'paused' ? '#fff3cd' :
                           raffle.status === 'completed' ? '#d1ecf1' :
                           raffle.status === 'cancelled' ? '#f8d7da' : '#e2e3e5',
                color: raffle.status === 'active' ? '#155724' :
                       raffle.status === 'paused' ? '#856404' :
                       raffle.status === 'completed' ? '#0c5460' :
                       raffle.status === 'cancelled' ? '#721c24' : '#383d41'
              }}>{raffle.status}</span></p>
              <p><strong>Total Entries:</strong> {raffle.total_entries || 0}</p>
              {raffle.statistics && (
                <p><strong>Unique Participants:</strong> {raffle.statistics.unique_participants}</p>
              )}
              {raffle.max_entries_per_person && (
                <p><strong>Max Entries Per Person:</strong> {raffle.max_entries_per_person}</p>
              )}
              <p><strong>Start Date:</strong> {new Date(raffle.start_date).toLocaleString()}</p>
              <p><strong>End Date:</strong> {new Date(raffle.end_date).toLocaleString()}</p>
              <p><strong>Timezone:</strong> {raffle.timezone}</p>
            </div>
          </>
        ) : (
          <div>
            <h2>Edit Raffle</h2>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Title:</label>
              <input
                type="text"
                value={editData.title}
                onChange={(e) => setEditData({...editData, title: e.target.value})}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Description:</label>
              <textarea
                value={editData.description}
                onChange={(e) => setEditData({...editData, description: e.target.value})}
                rows={4}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>End Date:</label>
              <input
                type="datetime-local"
                value={editData.end_date ? new Date(editData.end_date).toISOString().slice(0, 16) : ''}
                onChange={(e) => setEditData({...editData, end_date: e.target.value})}
                style={{ width: '100%', padding: '8px' }}
              />
            </div>
            {!hasEntries && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Max Entries Per Person:</label>
                <input
                  type="number"
                  value={editData.max_entries_per_person}
                  onChange={(e) => setEditData({...editData, max_entries_per_person: e.target.value})}
                  placeholder="Leave empty for unlimited"
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleUpdate} disabled={actionLoading}>Save Changes</button>
              <button onClick={() => setEditMode(false)} style={{ background: '#6c757d' }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Ticket Packages Section */}
        {packages.length > 0 && !editMode && (
          <div style={{
            marginTop: '30px',
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <h2>Ticket Packages</h2>
            {packages.map((pkg, index) => {
              const pricePerTicket = (parseFloat(pkg.price) / parseInt(pkg.ticket_quantity)).toFixed(2);
              const basePrice = packages[0] ? parseFloat(packages[0].price) / parseInt(packages[0].ticket_quantity) : 0;
              const savings = basePrice > 0 && pricePerTicket < basePrice
                ? (((basePrice - pricePerTicket) / basePrice) * 100).toFixed(1)
                : null;

              return (
                <div key={pkg.id} style={{
                  background: 'white',
                  padding: '15px',
                  marginBottom: '10px',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0' }}>{pkg.package_name}</h3>
                      <p style={{ margin: '5px 0', color: '#6c757d' }}>
                        {pkg.ticket_quantity} ticket{pkg.ticket_quantity > 1 ? 's' : ''}
                        {pkg.description && ` - ${pkg.description}`}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#6c757d' }}>
                        ${pricePerTicket} per ticket
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '0', color: '#007bff' }}>
                        ${parseFloat(pkg.price).toFixed(2)}
                      </p>
                      {savings && (
                        <p style={{
                          margin: '5px 0 0 0',
                          fontSize: '14px',
                          color: '#28a745',
                          fontWeight: 'bold'
                        }}>
                          Save {savings}%
                        </p>
                      )}
                    </div>
                  </div>
                  {pkg.ghl_product_id && (
                    <p style={{ fontSize: '12px', color: '#6c757d', margin: '10px 0 0 0' }}>
                      GHL Product ID: {pkg.ghl_product_id}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Winner Section */}
        {raffle.winner_name && (
          <div style={{
            background: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <h2 style={{ color: '#155724' }}>🎉 Winner</h2>
            <p><strong>Name:</strong> {raffle.winner_name}</p>
            <p><strong>Email:</strong> {raffle.winner_email}</p>
            <p><strong>Winning Ticket:</strong> {raffle.winning_ticket_number}</p>
            <p><strong>Selected:</strong> {new Date(raffle.winner_selected_at).toLocaleString()}</p>
          </div>
        )}

        {/* Action Buttons */}
        {!editMode && (
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {/* Edit Button - Always available unless completed or cancelled */}
            {!['completed', 'cancelled'].includes(raffle.status) && (
              <button onClick={() => setEditMode(true)} disabled={actionLoading}>
                Edit Raffle
              </button>
            )}

            {/* Activate - Only for draft */}
            {raffle.status === 'draft' && (
              <button onClick={() => handleAction('/activate', null, 'Raffle activated')} disabled={actionLoading}>
                Activate Raffle
              </button>
            )}

            {/* Pause - Only for active */}
            {raffle.status === 'active' && (
              <button
                onClick={() => handleAction('/pause', 'Pause this raffle?', 'Raffle paused')}
                disabled={actionLoading}
                style={{ background: '#ffc107', color: '#000' }}
              >
                Pause Raffle
              </button>
            )}

            {/* Resume - Only for paused */}
            {raffle.status === 'paused' && (
              <button
                onClick={() => handleAction('/resume', 'Resume this raffle?', 'Raffle resumed')}
                disabled={actionLoading}
                style={{ background: '#28a745' }}
              >
                Resume Raffle
              </button>
            )}

            {/* Deactivate - For active or paused */}
            {['active', 'paused'].includes(raffle.status) && (
              <button
                onClick={() => handleAction('/deactivate', 'Deactivate raffle and return to draft?', 'Raffle deactivated')}
                disabled={actionLoading}
                style={{ background: '#6c757d' }}
              >
                Deactivate
              </button>
            )}

            {/* Close & Select Winner - Only for active */}
            {raffle.status === 'active' && (
              <button
                onClick={() => handleAction('/close', 'Close raffle and select winner?')}
                disabled={actionLoading}
                style={{ background: '#17a2b8' }}
              >
                Close & Select Winner
              </button>
            )}

            {/* Cancel - For draft, active, or paused */}
            {['draft', 'active', 'paused'].includes(raffle.status) && (
              <button
                onClick={() => handleAction('/cancel', 'Cancel this raffle? This cannot be undone.', 'Raffle cancelled')}
                disabled={actionLoading}
                style={{ background: '#dc3545' }}
              >
                Cancel Raffle
              </button>
            )}

            {/* Delete - Only if NO entries */}
            {!hasEntries && !['completed'].includes(raffle.status) && (
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                style={{ background: '#dc3545' }}
              >
                Delete Raffle
              </button>
            )}

            {/* Info about why delete is disabled */}
            {hasEntries && !['completed', 'cancelled'].includes(raffle.status) && (
              <div style={{
                width: '100%',
                padding: '10px',
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                ⚠️ This raffle has {raffle.statistics.entry_count} entries and cannot be deleted. You can cancel it instead.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RaffleDetails;
